import { memo } from 'react'
import { useIntl } from 'react-intl'
import { Typography, FormControl } from '@mui/material'
import es from 'date-fns/locale/es'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { parse, isValid } from 'date-fns'

import { dateInputStyles } from './date-input.styles'

const REFUSE_DATE_PATTERN =
  '^(?:(?:31(\\/|-|\\.)(?:0?[13578]|1[02]))\\1|(?:(?:29|30)(\\/|-|\\.)(?:0?[13-9]|1[0-2])\\2))(?:(?:1[6-9]|[2-9]\\d)?\\d{2})$|^(?:29(\\/|-|\\.)0?2\\3(?:(?:(?:1[6-9]|[2-9]\\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\\d|2[0-8])(\\/|-|\\.)(?:(?:0?[1-9])|(?:1[0-2]))\\4(?:(?:1[6-9]|[2-9]\\d)?\\d{2})$'

/**
 * @typedef {import('@mui/x-date-pickers/DatePicker').DatePickerProps<Date>} MuiDatePickerProps
 */

/**
 * @typedef {object} DateChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {string | null} target.value - El nuevo valor de la fecha como una cadena en formato ISO 8601 (e.g., "2024-12-12T00:00:00.000Z"), o `null` si el campo se vacía.
 */

/**
 * @typedef {object} DateInputProps
 * @property {boolean} [readOnly=false] - Si es `true`, muestra la fecha como texto estático en lugar de un campo de entrada.
 * @property {Date} [minDate] - La fecha mínima que se puede seleccionar en el calendario.
 * @property {Date} [maxDate] - La fecha máxima que se puede seleccionar en el calendario.
 * @property {boolean} [allowKeyboard=true] - Si es `false`, deshabilita la entrada por teclado, forzando al usuario a usar únicamente el selector de calendario.
 * @property {string} title - El título o etiqueta que se muestra sobre el campo de fecha.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onChange`.
 * @property {Date | string | null} value - El valor actual de la fecha. Puede ser un objeto Date, una cadena de fecha válida o `null`.
 * @property {function(DateChangeEvent): void} onChange - La función callback que se ejecuta cuando se selecciona una fecha.
 */

/**
 * Un componente de entrada de fecha controlado, construido sobre `DatePicker` de MUI X.
 *
 * Está preconfigurado para usar el adaptador `date-fns` con localización en español.
 * Ofrece un modo de solo lectura (`readOnly`) y normaliza el valor de salida a una
 * cadena en formato ISO 8601 (e.g., "2024-12-12T00:00:00.000Z") a través del callback `onChange`.
 * El componente está optimizado con `React.memo`.
 *
 * @component
 * @example
 * const MyDateForm = () => {
 * const [birthDate, setBirthDate] = useState('1990-10-20T00:00:00.000Z');
 *
 * const handleChange = (e) => {
 * console.log(`Campo: ${e.target.name}, Valor: ${e.target.value}`);
 * setBirthDate(e.target.value);
 * };
 *
 * return (
 * <DateInput
 * title="Fecha de Nacimiento"
 * name="birthDate"
 * value={birthDate}
 * onChange={handleChange}
 * maxDate={new Date()} // No se puede nacer en el futuro
 * />
 * );
 * };
 *
 * @param {DateInputProps & MuiDatePickerProps} props - Las props del componente DateInput.
 */
export const DateInput = memo(
  ({
    readOnly = false,
    minDate,
    maxDate,
    allowKeyboard = true,
    title,
    fontSize,
    name,
    value,
    onChange,
    ...rest
  }) => {
    const { formatDate } = useIntl()
    const typographyVariant = fontSize === 'small' ? '2' : '1'

    const parseDate = (dateValue) => {
      if (!dateValue) return null
      if (dateValue instanceof Date) return dateValue

      const parsedDate = parse(dateValue, 'dd/MM/yyyy', new Date())
      if (isValid(parsedDate)) return parsedDate

      const date = new Date(dateValue)
      if (isValid(date)) {
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
      }

      return null
    }

    const handleChange = (newDate) => {
      if (newDate && isValid(newDate)) {
        const normalizedDate = new Date(
          Date.UTC(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), 0, 0, 0, 0)
        )
        const isoDate = normalizedDate.toISOString()
        onChange({ target: { name, value: isoDate } })
      } else {
        onChange({ target: { name, value: null } })
      }
    }

    if (readOnly) {
      const formatReadOnlyDate = (dateValue) => {
        if (!dateValue) return '--'
        try {
          const date = new Date(dateValue)
          const year = date.getUTCFullYear()
          const month = String(date.getUTCMonth() + 1).padStart(2, '0')
          const day = String(date.getUTCDate()).padStart(2, '0')
          return `${day}/${month}/${year}`
        } catch {
          return '--'
        }
      }

      return (
        <FormControl sx={{ width: '100%' }} margin="none">
          {title && (
            <Typography variant={`subtitle${typographyVariant}`} sx={dateInputStyles.title}>
              {title}
            </Typography>
          )}
          <Typography
            variant={`body${typographyVariant}`}
            color="textSecondary"
            sx={{ whiteSpace: 'pre-line' }}
          >
            {formatReadOnlyDate(value)}
          </Typography>
        </FormControl>
      )
    }

    return (
      <FormControl sx={{ width: '100%' }} margin="none">
        {title && (
          <Typography variant={`subtitle${typographyVariant}`} sx={dateInputStyles.title}>
            {title}
          </Typography>
        )}
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <DatePicker
            {...rest}
            value={parseDate(value)}
            onChange={handleChange}
            minDate={minDate}
            maxDate={maxDate}
            format="dd/MM/yyyy"
            readOnly={!allowKeyboard}
            slotProps={{
              textField: {
                id: name,
                name: name,
                variant: 'outlined',
                sx: dateInputStyles.input,
                inputProps: {
                  pattern: REFUSE_DATE_PATTERN,
                },
              },
              actionBar: {
                actions: ['clear', 'cancel', 'accept'],
              },
            }}
          />
        </LocalizationProvider>
      </FormControl>
    )
  }
)
